import { useQuery } from '@tanstack/react-query';
import { http, returnIfNotFoundError } from 'services/http';
import { DOCUMENTS_GET_MANY } from 'api/query-keys.ts';
import type { DocumentRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query';
import type { EDocumentSearchField } from 'types/document.ts';

interface IParams extends IWithPagination {
  field: EDocumentSearchField;
  entityId: number;
  withUrl?: boolean;
  enabled?: boolean;
}

export const useGetDocuments = (params: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, field, entityId, withUrl, enabled = true } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    'filter[field]': String(field),
    'filter[entityId]': String(entityId),
  });
  if (withUrl !== undefined) qs.set('withUrl', String(withUrl));

  return useQuery<WithPagination<DocumentRes[]>>({
    queryKey: [DOCUMENTS_GET_MANY, { field, entityId, page, limit, withUrl }],
    enabled,
    queryFn: async () => {
      try {
        const { data, headers } = await http.get<DocumentRes[]>(`/v1/documents?${qs.toString()}`);
        const pagination = preparePagination(headers);

        return { data, pagination };
      } catch (error) {
        return returnIfNotFoundError<WithPagination<[]>>(error, { data: [], pagination: null });
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    cacheTime: 30 * 1000,
  });
};
